import { useContext } from 'react';
import { useRouter } from 'next/router';
import { useSession, useUser } from '@descope/nextjs-sdk/client';
import { PageContext } from '@/context/PageContext';
import { setEfgTokenProvider } from 'src/service/engageFrontendGateway';
import { PREFERENCES_ONLY_SCOPE, readTokenScope, readTokenSubject } from './tokenScope';
import { useMockSignedIn } from './mockResidentSession';

/** The one route a `preferences_only` session is allowed to act on. */
const PREFERENCES_ROUTE_SUFFIX = '/preferences';

// Subset of Descope's UserResponse the resident UI actually consumes.
// Derived (not duplicated) so SDK shape changes surface here, not at call sites.
type DescopeUser = ReturnType<typeof useUser>['user'];
export type ResidentUser = Pick<NonNullable<DescopeUser>, 'name' | 'email' | 'phone'>;

const MOCK_USER: ResidentUser = {
  name: 'Mock Resident',
  email: 'mock.resident@example.com',
  phone: '+1 555 000 0000',
};

// Compile-time stripped from production builds so the `/engage_mock/...`
// slug can never bypass auth in prod, regardless of who navigates there.
const MOCK_ORG_ENABLED = process.env.NODE_ENV !== 'production';

export const isMockResidentOrg = (organization?: string): boolean => MOCK_ORG_ENABLED && organization === 'engage_mock';

export interface ResidentSession {
  isAuthenticated: boolean;
  isSessionLoading: boolean;
  isMock: boolean;
  user: ResidentUser | undefined;
  isUserLoading: boolean;
  /**
   * Session came from an emailed preferences link, so it may only manage
   * communication preferences (ENG-3878). Stays true off the preferences route,
   * where `isAuthenticated` is forced false — callers that need to tell "restricted"
   * from "signed out" read this.
   */
  isPreferencesOnly: boolean;
  /**
   * The Descope user the gateway token acts as, read from its `sub`; undefined when no token is
   * sent (signed out, or the mock session). It changes when another resident signs in, so work
   * started for one resident can stop before the next one's token carries it.
   */
  sessionSubject: string | undefined;
}

// Under the `engage_mock` org the session is the dev-only mock session (signed in with a mock
// user unless `?session=out`), so the user menu and resident-only pages render in dev without a real
// Descope session.
// Also syncs the EFG axios token provider so authenticated requests carry
// `Authorization: Bearer <jwt>`. Mock org intentionally exposes no token —
// mock pages must short-circuit network calls.
// `organization` arg is for page-level callers that run before their own
// `PageContext.Provider` renders; child components can omit it.
export function useResidentSession(organization?: string): ResidentSession {
  const { urlParams } = useContext(PageContext);
  const router = useRouter();
  const session = useSession();
  const { user, isUserLoading } = useUser();
  const org = organization ?? urlParams?.organization;
  const isMock = isMockResidentOrg(org);
  const isMockSignedIn = useMockSignedIn();

  const efgToken = !isMock && session.isAuthenticated && session.sessionToken ? session.sessionToken : undefined;
  const isPreferencesOnly = readTokenScope(efgToken) === PREFERENCES_ONLY_SCOPE;

  // The gateway withholds a scoped token outside its allowlisted routes, which
  // makes those requests anonymous rather than rejected. Presenting a signed-in UI
  // there would record a survey submission or a map pin with no resident attached,
  // so off the preferences route a scoped session reads as signed out instead.
  const isPreferencesRoute = Boolean(router?.pathname?.endsWith(PREFERENCES_ROUTE_SUFFIX));
  const isAuthenticated = session.isAuthenticated && (!isPreferencesOnly || isPreferencesRoute);

  // Scope gating lives in the gateway's request interceptor, not here — one
  // decision point for every provider that registers a token.
  setEfgTokenProvider(() => efgToken);

  if (isMock) {
    return {
      isAuthenticated: isMockSignedIn,
      isSessionLoading: false,
      isMock: true,
      user: isMockSignedIn ? MOCK_USER : undefined,
      isUserLoading: false,
      isPreferencesOnly: false,
      sessionSubject: undefined,
    };
  }
  return {
    isAuthenticated,
    isSessionLoading: session.isSessionLoading,
    isMock: false,
    user,
    isUserLoading,
    isPreferencesOnly,
    sessionSubject: readTokenSubject(efgToken),
  };
}
